import { useCallback, useEffect, useState } from "react";
import { App, Alert, Button, Flex, Form, Input, InputNumber, Modal, Switch, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ContractPlan,
  ContractPlanProvider,
  PlanInput,
  useContractPlans,
} from "../../context/contractPlanContext";
import { useTranslation } from "react-i18next";

/** プラン作成/編集モーダルのフォーム値。空文字を許容するため description は string。 */
type PlanFormValues = {
  code: string;
  name: string;
  description: string;
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  rate_limit_image_per_minute: number;
  max_page_size: number;
  max_api_keys_per_company: number;
  max_redeliveries_per_day: number;
  allow_ip_restriction: boolean;
};

const CREATE_DEFAULTS: PlanFormValues = {
  code: "",
  name: "",
  description: "",
  rate_limit_per_minute: 0,
  rate_limit_per_hour: 0,
  rate_limit_image_per_minute: 0,
  // バックエンド検証は max_* に最小値 1 を要求するため、既定値は DB デフォルト (100/5/10) に揃える。
  max_page_size: 100,
  max_api_keys_per_company: 5,
  max_redeliveries_per_day: 10,
  allow_ip_restriction: false,
};

/** 既存プランをフォーム値に変換する (full-replace 編集の pre-fill 用)。 */
function planToFormValues(plan: ContractPlan): PlanFormValues {
  return {
    code: plan.code,
    name: plan.name,
    description: plan.description ?? "",
    rate_limit_per_minute: plan.rate_limit_per_minute,
    rate_limit_per_hour: plan.rate_limit_per_hour,
    rate_limit_image_per_minute: plan.rate_limit_image_per_minute,
    max_page_size: plan.max_page_size,
    max_api_keys_per_company: plan.max_api_keys_per_company,
    max_redeliveries_per_day: plan.max_redeliveries_per_day,
    allow_ip_restriction: plan.allow_ip_restriction,
  };
}

function toPlanInput(v: PlanFormValues): PlanInput {
  return {
    code: v.code.trim(),
    name: v.name.trim(),
    description: v.description.trim() ? v.description.trim() : null,
    rate_limit_per_minute: v.rate_limit_per_minute,
    rate_limit_per_hour: v.rate_limit_per_hour,
    rate_limit_image_per_minute: v.rate_limit_image_per_minute,
    max_page_size: v.max_page_size,
    max_api_keys_per_company: v.max_api_keys_per_company,
    max_redeliveries_per_day: v.max_redeliveries_per_day,
    allow_ip_restriction: v.allow_ip_restriction,
  };
}

/** プラン入力フォーム本体 (作成/編集共用)。編集時は code を読み取り専用にする。 */
function PlanFormFields({
  form,
  codeReadOnly,
  initialValues,
}: {
  form: ReturnType<typeof Form.useForm<PlanFormValues>>[0];
  codeReadOnly: boolean;
  initialValues: PlanFormValues;
}) {
  const { t } = useTranslation();
  // preserve={false}: destroyOnClose と併用し、閉じる際に store を破棄する。
  // これがないと永続 form インスタンスに前回プランの値が残り、full-replace PUT で
  // 別プランの値を誤って上書きする事故になり得る (codex 指摘)。
  return (
    <Form form={form} layout="vertical" style={{ marginTop: 12 }} initialValues={initialValues} preserve={false}>
      <Form.Item
        label={t("contractPlan.fields.code")}
        name="code"
        rules={[{ required: true, message: t("contractPlan.validation.codeRequired") }, { max: 64, message: t("contractPlan.validation.max64") }]}
        extra={codeReadOnly ? t("contractPlan.codeReadOnly") : undefined}
      >
        <Input maxLength={64} disabled={codeReadOnly} placeholder={t("contractPlan.placeholders.code")} />
      </Form.Item>
      <Form.Item
        label={t("contractPlan.fields.name")}
        name="name"
        rules={[{ required: true, message: t("contractPlan.validation.nameRequired") }, { max: 255, message: t("contractPlan.validation.max255") }]}
      >
        <Input maxLength={255} placeholder={t("contractPlan.placeholders.name")} />
      </Form.Item>
      <Form.Item label={t("contractPlan.fields.description")} name="description">
        <Input.TextArea rows={2} maxLength={1000} placeholder={t("contractPlan.placeholders.description")} />
      </Form.Item>

      <Flex gap="middle" wrap>
        <Form.Item label={t("contractPlan.fields.rateLimitPerMinute")} name="rate_limit_per_minute" rules={[{ required: true, message: t("contractPlan.validation.required") }]}>
          <InputNumber min={0} precision={0} style={{ width: 160 }} />
        </Form.Item>
        <Form.Item label={t("contractPlan.fields.rateLimitPerHour")} name="rate_limit_per_hour" rules={[{ required: true, message: t("contractPlan.validation.required") }]}>
          <InputNumber min={0} precision={0} style={{ width: 160 }} />
        </Form.Item>
        <Form.Item label={t("contractPlan.fields.rateLimitImagePerMinute")} name="rate_limit_image_per_minute" rules={[{ required: true, message: t("contractPlan.validation.required") }]}>
          <InputNumber min={0} precision={0} style={{ width: 160 }} />
        </Form.Item>
      </Flex>
      <Flex gap="middle" wrap>
        {/* max_* はバックエンド検証が最小値 1 を要求するため min={1} (0 入力は 400 になる) */}
        <Form.Item label={t("contractPlan.fields.maxPageSize")} name="max_page_size" rules={[{ required: true, message: t("contractPlan.validation.required") }]}>
          <InputNumber min={1} precision={0} style={{ width: 160 }} />
        </Form.Item>
        <Form.Item label={t("contractPlan.fields.maxApiKeysPerCompany")} name="max_api_keys_per_company" rules={[{ required: true, message: t("contractPlan.validation.required") }]}>
          <InputNumber min={1} precision={0} style={{ width: 160 }} />
        </Form.Item>
        <Form.Item label={t("contractPlan.fields.maxRedeliveriesPerDay")} name="max_redeliveries_per_day" rules={[{ required: true, message: t("contractPlan.validation.required") }]}>
          <InputNumber min={1} precision={0} style={{ width: 160 }} />
        </Form.Item>
      </Flex>
      <Form.Item label={t("contractPlan.fields.allowIpRestriction")} name="allow_ip_restriction" valuePropName="checked">
        <Switch />
      </Form.Item>
    </Form>
  );
}

function ContractPlanPageInner() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { plans, loading, listPlans, createPlan, updatePlan, deactivatePlan } = useContractPlans();

  const [createForm] = Form.useForm<PlanFormValues>();
  const [editForm] = Form.useForm<PlanFormValues>();

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [editTarget, setEditTarget] = useState<ContractPlan | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    listPlans();
  }, [listPlans]);

  const openCreate = useCallback(() => {
    // destroyOnClose のため Form は開くたびに再マウントされ、initialValues={CREATE_DEFAULTS} で初期化される
    setCreateOpen(true);
  }, []);

  const submitCreate = useCallback(async () => {
    let values: PlanFormValues;
    try {
      values = await createForm.validateFields();
    } catch {
      return;
    }
    setCreating(true);
    try {
      const result = await createPlan(toPlanInput(values));
      if (!result.ok) {
        message.error(result.message);
        return;
      }
      message.success(t("contractPlan.created"));
      setCreateOpen(false);
      listPlans();
    } finally {
      setCreating(false);
    }
  }, [createForm, createPlan, message, listPlans, t]);

  // full-replace のため全フィールドを行から埋める (作成/編集 Form 共用の初期表示用)。
  const editInitialValues: PlanFormValues | null = editTarget ? planToFormValues(editTarget) : null;

  // 編集モーダルを開いた後、対象プランの値をフォームへ明示セットする。
  // antd の Form インスタンス (Form.useForm) は永続で、initialValues は再マウント時に
  // 確実には再適用されない (前回プランの値が残る → full-replace PUT で誤上書きの恐れ)。
  // そのため editTarget 変更時に setFieldsValue で必ず上書きする。
  useEffect(() => {
    if (editTarget) {
      editForm.setFieldsValue(planToFormValues(editTarget));
    }
  }, [editTarget, editForm]);

  // 作成モーダルを開くたびに既定値へリセットする (前回の入力残りを防ぐ)。
  useEffect(() => {
    if (createOpen) {
      createForm.setFieldsValue(CREATE_DEFAULTS);
    }
  }, [createOpen, createForm]);

  const openEdit = useCallback((plan: ContractPlan) => {
    setEditTarget(plan);
  }, []);

  const submitEdit = useCallback(async () => {
    if (!editTarget) return;
    let values: PlanFormValues;
    try {
      values = await editForm.validateFields();
    } catch {
      return;
    }
    setEditing(true);
    try {
      // full-replace: code 以外の全フィールドを送る (toPlanInput が全項目を含む)
      const result = await updatePlan(editTarget.id, toPlanInput(values));
      if (!result.ok) {
        message.error(result.message);
        return;
      }
      message.success(t("contractPlan.updated"));
      setEditTarget(null);
      listPlans();
    } finally {
      setEditing(false);
    }
  }, [editTarget, editForm, updatePlan, message, listPlans, t]);

  const onDeactivate = useCallback(
    (plan: ContractPlan) => {
      Modal.confirm({
        title: t("contractPlan.deactivateConfirmTitle", { name: plan.name }),
        content: t("contractPlan.deactivateConfirmContent"),
        okText: t("contractPlan.deactivate"),
        okButtonProps: { danger: true },
        cancelText: t("common.cancel"),
        onOk: async () => {
          const result = await deactivatePlan(plan.id);
          if (!result.ok) {
            message.error(result.message);
            return;
          }
          message.success(t("contractPlan.deactivated"));
          listPlans();
        },
      });
    },
    [deactivatePlan, message, listPlans, t]
  );

  const columns: ColumnsType<ContractPlan> = [
    { title: t("contractPlan.columns.code"), dataIndex: "code", key: "code", render: (v) => <code>{v}</code> },
    { title: t("contractPlan.columns.name"), dataIndex: "name", key: "name" },
    { title: t("contractPlan.columns.description"), dataIndex: "description", key: "description", render: (v: string | null) => v || "-" },
    { title: t("contractPlan.columns.rateMinute"), dataIndex: "rate_limit_per_minute", key: "rate_limit_per_minute" },
    { title: t("contractPlan.columns.rateHour"), dataIndex: "rate_limit_per_hour", key: "rate_limit_per_hour" },
    { title: t("contractPlan.columns.imageRateMinute"), dataIndex: "rate_limit_image_per_minute", key: "rate_limit_image_per_minute" },
    { title: t("contractPlan.columns.maxApiKeys"), dataIndex: "max_api_keys_per_company", key: "max_api_keys_per_company" },
    {
      title: t("contractPlan.columns.ipRestrictionAllowed"),
      dataIndex: "allow_ip_restriction",
      key: "allow_ip_restriction",
      render: (v: boolean) => <Tag color={v ? "blue" : "default"}>{v ? t("contractPlan.allowed") : t("contractPlan.notAllowed")}</Tag>,
    },
    {
      title: t("common.status"),
      dataIndex: "is_active",
      key: "is_active",
      render: (v: boolean) => <Tag color={v ? "green" : "red"}>{v ? t("common.active") : t("common.inactive")}</Tag>,
    },
    {
      title: t("common.operation"),
      key: "actions",
      render: (_: unknown, plan: ContractPlan) =>
        plan.is_active ? (
          <Flex gap="small">
            <Button size="small" onClick={() => openEdit(plan)}>
              {t("common.edit")}
            </Button>
            <Button size="small" danger onClick={() => onDeactivate(plan)}>
              {t("contractPlan.deactivate")}
            </Button>
          </Flex>
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>{t("contractPlan.title")}</Typography.Title>

      <Alert type="info" showIcon message={t("contractPlan.propagationNote")} style={{ marginBottom: 16 }} />

      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary">{t("contractPlan.listNote")}</Typography.Text>
        <Button type="primary" onClick={openCreate}>
          {t("contractPlan.createButton")}
        </Button>
      </Flex>

      <Table rowKey="id" loading={loading} columns={columns} dataSource={plans} pagination={false} />

      {/* 作成モーダル */}
      <Modal
        title={t("contractPlan.createTitle")}
        open={createOpen}
        onOk={submitCreate}
        confirmLoading={creating}
        onCancel={() => setCreateOpen(false)}
        okText={t("contractPlan.createOk")}
        cancelText={t("common.cancel")}
        width={680}
        destroyOnClose
      >
        <PlanFormFields form={createForm} codeReadOnly={false} initialValues={CREATE_DEFAULTS} />
      </Modal>

      {/* 編集モーダル (full-replace。code 以外の全項目を編集して送信する) */}
      <Modal
        title={editTarget ? t("contractPlan.editTitleWithCode", { code: editTarget.code }) : t("contractPlan.editTitle")}
        open={editTarget !== null}
        onOk={submitEdit}
        confirmLoading={editing}
        onCancel={() => setEditTarget(null)}
        okText={t("contractPlan.updateOk")}
        cancelText={t("common.cancel")}
        width={680}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          message={t("contractPlan.editWarning")}
          style={{ marginBottom: 8 }}
        />
        {editInitialValues && <PlanFormFields form={editForm} codeReadOnly initialValues={editInitialValues} />}
      </Modal>
    </div>
  );
}

export default function ContractPlanPage() {
  return (
    <ContractPlanProvider>
      <ContractPlanPageInner />
    </ContractPlanProvider>
  );
}
